import { z } from 'zod'

import { LOAN_ACCOUNT_STATUSES, LOAN_PRODUCT_STATUSES } from '@/features/loans/constants'

/**
 * The loan screens keep their state in the URL, for the same reason the customer and group
 * screens do: a filtered list is a place. "Which accounts at Madina are in arrears?" should be a
 * link somebody can send, and pressing Back out of a round should land on the page of the list it
 * was opened from.
 *
 * Every value is read through `.catch()`, so a hand-edited or stale URL degrades to the default
 * rather than throwing an operator out of the screen. `parseLoanSearch` applies the same schemas
 * inside the components, which is what lets these screens work before the integrator has attached
 * `validateSearch` to the routes — and unchanged afterwards.
 */

export const DEFAULT_PAGE_SIZE = 25

export const loanAccountListSearchSchema = z.object({
  search: z.string().trim().min(1).optional().catch(undefined),
  status: z.enum(LOAN_ACCOUNT_STATUSES).optional().catch(undefined),
  branchId: z.string().min(1).optional().catch(undefined),
  /** Set when the list was opened from a customer's file. */
  customerId: z.string().min(1).optional().catch(undefined),
  /** Set when it was opened from a group. */
  groupId: z.string().min(1).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z.number().int().min(10).max(200).optional().catch(undefined),
})

export type LoanAccountListSearch = z.infer<typeof loanAccountListSearchSchema>

export const LOAN_ACCOUNT_TABS = ['rounds', 'overview', 'prerequisites'] as const

export type LoanAccountTab = (typeof LOAN_ACCOUNT_TABS)[number]

export const loanAccountDetailSearchSchema = z.object({
  tab: z.enum(LOAN_ACCOUNT_TABS).optional().catch(undefined),
})

export const LOAN_CYCLE_TABS = [
  'round',
  'schedule',
  'terms',
  'fees',
  'guarantees',
  'classification',
] as const

export type LoanCycleTab = (typeof LOAN_CYCLE_TABS)[number]

export const loanCycleDetailSearchSchema = z.object({
  tab: z.enum(LOAN_CYCLE_TABS).optional().catch(undefined),
})

export const loanProductListSearchSchema = z.object({
  search: z.string().trim().min(1).optional().catch(undefined),
  status: z.enum(LOAN_PRODUCT_STATUSES).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z.number().int().min(10).max(200).optional().catch(undefined),
})

export type LoanProductListSearch = z.infer<typeof loanProductListSearchSchema>

/**
 * Parses whatever the router handed over.
 *
 * The schemas above are exported for the router to validate with; this applies them again inside
 * the screen so that a route registered without `validateSearch` still produces defaults rather
 * than `undefined` fields nobody checked. Parsing twice costs nothing and removes an ordering
 * dependency between this area and the file that wires it.
 */
export function parseLoanSearch<TSchema extends z.ZodType>(
  schema: TSchema,
  raw: unknown,
): z.infer<TSchema> {
  const parsed = schema.safeParse(raw ?? {})
  return parsed.success ? parsed.data : schema.parse({})
}
